import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Users2, Plus, Trash2, Save, Loader2, Upload, Download, X, DollarSign } from "lucide-react";

const BLANK = { name: "", default_direct_rate: 75, description: "", tags: [], notes: "" };

const fmt = (v, d = 2) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: d });

/**
 * Labor category rate card — CSV import + editable table. Rates auto-feed
 * the services-mode Estimator prompt so Claude uses your real rates per role.
 */
export default function LaborCategoriesPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/labor-categories"); setRows(r.data || []); }
    catch { toast.error("Could not load labor categories"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startEdit = (row) => { setEditing(row.cat_id); setForm({ ...BLANK, ...row, tags: row.tags || [] }); };
  const startNew = () => { setEditing("new"); setForm(BLANK); };
  const cancel = () => { setEditing(null); setForm(BLANK); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const body = { ...form, tags: Array.isArray(form.tags) ? form.tags : String(form.tags || "").split(",").map(t => t.trim()).filter(Boolean) };
      if (editing === "new") await api.post("/labor-categories", body);
      else await api.put(`/labor-categories/${editing}`, body);
      toast.success("Saved");
      cancel();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    try { await api.delete(`/labor-categories/${row.cat_id}`); toast.success("Removed"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/labor-categories/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { created, updated, skipped, errors } = r.data;
      toast.success(`Import done — ${created} new, ${updated} updated, ${skipped} skipped`);
      if (errors?.length) toast.error(`${errors.length} row(s) had issues — first: ${errors[0]}`);
      load();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const sampleCsv = "name,direct_rate,description,tags\nProgram Manager,105,Manages the full engagement,pm;senior\nCyber Analyst,95,SOC monitoring & IR support,cyber;mid\nJunior Analyst,52,Entry-level analyst,junior\n";
  const downloadSample = () => {
    const blob = new Blob([sampleCsv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "labor-categories-template.csv"; document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const exportCsv = async () => {
    try {
      const r = await api.get("/labor-categories/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = "labor-categories.csv"; document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} role${rows.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    }
  };

  return (
    <div className="mt-10 rounded-2xl bg-white border border-slate-200 p-6" data-testid="labor-categories-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <div className="text-[10px] font-mono font-bold tracking-widest text-indigo-700 mb-1">// LABOR CATEGORY RATE CARD</div>
          <div className="font-display font-black text-2xl text-slate-900">Roles &amp; direct rates</div>
          <p className="text-sm text-slate-500 mt-1 max-w-xl">
            Your workspace roster of labor categories and their default direct hourly rates. The services-mode Estimator uses these rates verbatim when a role matches — no more guessing from generic ranges.
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <button onClick={downloadSample} className="text-xs font-mono font-bold tracking-widest text-slate-500 hover:text-slate-800 inline-flex items-center gap-1.5" data-testid="lc-sample-csv">
            <Download className="w-3.5 h-3.5" />CSV TEMPLATE
          </button>
          <button onClick={exportCsv} disabled={rows.length === 0} className="px-4 py-2 rounded-full text-xs tracking-widest uppercase font-display font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-200 inline-flex items-center gap-2 disabled:opacity-40" data-testid="lc-export-btn">
            <Download className="w-4 h-4" />EXPORT CSV
          </button>
          <input ref={fileRef} onChange={importCsv} type="file" accept=".csv,.tsv,text/csv" className="hidden" data-testid="lc-file-input" />
          <button onClick={() => fileRef.current?.click()} disabled={importing} className="px-4 py-2 rounded-full text-xs tracking-widest uppercase font-display font-bold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 inline-flex items-center gap-2 disabled:opacity-50" data-testid="lc-import-btn">
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}IMPORT CSV
          </button>
          <button onClick={startNew} className="bf-pill px-4 py-2 text-xs tracking-widest uppercase font-display font-bold inline-flex items-center gap-2" data-testid="lc-add-btn">
            <Plus className="w-4 h-4" />NEW ROLE
          </button>
        </div>
      </div>

      {editing && (
        <div className="mb-5 rounded-xl bg-indigo-50/50 border border-indigo-200 p-4" data-testid="lc-form">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-1">NAME *</div>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Senior Program Manager" className="w-full rounded-xl bg-white border border-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 px-3 py-2 text-sm outline-none" data-testid="lc-name" />
            </div>
            <div>
              <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-1">DIRECT RATE ($/hr) *</div>
              <input type="number" step="0.01" value={form.default_direct_rate} onChange={(e) => setForm({ ...form, default_direct_rate: parseFloat(e.target.value) || 0 })} className="w-full rounded-xl bg-white border border-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 px-3 py-2 text-sm outline-none font-mono" data-testid="lc-rate" />
            </div>
            <div className="md:col-span-2">
              <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-1">DESCRIPTION</div>
              <input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Clearance level, seniority, notes for the AI matcher…" className="w-full rounded-xl bg-white border border-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 px-3 py-2 text-sm outline-none" data-testid="lc-description" />
            </div>
            <div className="md:col-span-2">
              <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-1">TAGS (comma-separated)</div>
              <input value={Array.isArray(form.tags) ? form.tags.join(", ") : (form.tags || "")} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="pm, cleared, cyber" className="w-full rounded-xl bg-white border border-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 px-3 py-2 text-sm outline-none" data-testid="lc-tags" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={cancel} className="text-xs font-mono font-bold tracking-widest text-slate-500 hover:text-slate-800 px-4 py-2" data-testid="lc-cancel">CANCEL</button>
            <button onClick={save} disabled={saving} className="bf-pill px-4 py-2 text-xs tracking-widest uppercase font-display font-bold inline-flex items-center gap-2 disabled:opacity-50" data-testid="lc-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}SAVE
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="p-8 text-center text-slate-500 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl bg-slate-50 border border-dashed border-slate-300 p-8 text-center" data-testid="lc-empty">
          <Users2 className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <div className="font-display font-bold text-slate-800 mb-1">No labor categories yet</div>
          <div className="text-sm text-slate-500 mb-3">Upload a CSV or add roles one at a time so services-mode estimates use your real rates.</div>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] font-mono font-bold tracking-widest text-slate-500">
              <tr>
                <th className="px-4 py-2.5 text-left">ROLE</th>
                <th className="px-4 py-2.5 text-right">DIRECT $/HR</th>
                <th className="px-4 py-2.5 text-left">DESCRIPTION</th>
                <th className="px-4 py-2.5 text-left">TAGS</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.cat_id} className="hover:bg-indigo-50/30" data-testid={`lc-row-${r.cat_id}`}>
                  <td className="px-4 py-2.5 font-semibold text-slate-900">{r.name}</td>
                  <td className="px-4 py-2.5 text-right font-mono font-bold text-indigo-700">${fmt(r.default_direct_rate)}</td>
                  <td className="px-4 py-2.5 text-slate-600 text-xs">{r.description || "—"}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {(r.tags || []).map((t, i) => (
                        <span key={i} className="text-[9px] font-mono font-bold tracking-wider text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-full px-1.5 py-0.5">{t}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <button onClick={() => startEdit(r)} className="text-xs text-indigo-600 hover:text-indigo-800 font-semibold mr-3" data-testid={`lc-edit-${r.cat_id}`}>EDIT</button>
                    <button onClick={() => remove(r)} className="text-slate-300 hover:text-rose-600" data-testid={`lc-remove-${r.cat_id}`} aria-label="Remove">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
